// Transform point by matrix
//
export const applyTransform = (p, m) => {
  return [p[0] * m[0] + p[1] * m[2] + m[4], p[0] * m[1] + p[1] * m[3] + m[5]];
};

// Transform point by matrix inverse
//
export const applyInverseTransform = (p, m) => {
  const d = m[0] * m[3] - m[1] * m[2];
  return [
    (p[0] * m[3] - p[1] * m[2] + m[2] * m[5] - m[4] * m[3]) / d,
    (-p[0] * m[1] + p[1] * m[0] + m[4] * m[1] - m[5] * m[0]) / d,
  ];
};

// Concatenates two transformation matrices together and returns the result.
export const transform = (m1, m2) => {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
};

export const translate = (m, x, y) => {
  return [
    m[0],
    m[1],
    m[2],
    m[3],
    m[0] * x + m[2] * y + m[4],
    m[1] * x + m[3] * y + m[5],
  ];
};

export const rotate = (m, angle) => {
  angle = (angle * Math.PI) / 180;

  const cosValue = Math.cos(angle);
  const sinValue = Math.sin(angle);

  return [
    m[0] * cosValue + m[2] * sinValue,
    m[1] * cosValue + m[3] * sinValue,
    m[0] * -sinValue + m[2] * cosValue,
    m[1] * -sinValue + m[3] * cosValue,
    m[4],
    m[5],
  ];
};

export const scale = (m, x, y) => {
  return [m[0] * x, m[1] * x, m[2] * y, m[3] * y, m[4], m[5]];
};

export const makePoint = (x, y, z) => {
  return { x: x, y: y, z: z };
};

export const makeVector = (xcoord, ycoord, zcoord) => {
  return { xcoord: xcoord, ycoord: ycoord, zcoord: zcoord };
};

export const makeVectorFromPoints = (pt1, pt2) => {
  const xcoord = pt2.x - pt1.x;
  const ycoord = pt2.y - pt1.y;
  const zcoord = pt2.z - pt1.z;
  return makeVector(xcoord, ycoord, zcoord);
};

export const addVector = (pt, v) => {
  return makePoint(pt.x + v.xcoord, pt.y + v.ycoord, pt.z + v.zcoord);
};

export const multiplyVector = (v, scalar) => {
  return makeVector(v.xcoord * scalar, v.ycoord * scalar, v.zcoord * scalar);
};

export const magnitude = (v) => {
  return Math.sqrt(
    Math.pow(v.xcoord, 2) + Math.pow(v.ycoord, 2) + Math.pow(v.zcoord, 2)
  );
};

export const negateVector = (v) => {
  return multiplyVector(v, -1);
};

export const unitVector = (v) => {
  const mag = magnitude(v);
  const xcoord = v.xcoord / mag;
  const ycoord = v.ycoord / mag;
  const zcoord = v.zcoord / mag;
  return makeVector(xcoord, ycoord, zcoord);
};

export const crossProduct = (u, v) => {
  //
  // u X v = < u2*v3 - u3*v2,
  //           u3*v1 - u1*v3,
  //           u1*v2 - u2*v1 >
  const xcoord = u.ycoord * v.zcoord - u.zcoord * v.ycoord;
  const ycoord = u.zcoord * v.xcoord - u.xcoord * v.zcoord;
  const zcoord = u.xcoord * v.ycoord - u.ycoord * v.xcoord;
  return makeVector(xcoord, ycoord, zcoord);
};

/**
 * Determine if a point intersects a rect
 *
 * @param {Number} x The x coordinate of the point
 * @param {Number} y The y coordinate of the point
 * @param {Object} rect The points of a rect (likely from getBoundingClientRect)
 * @return {Boolean} True if a collision occurs, otherwise false
 */
export const pointIntersectsRect = (x, y, rect) => {
  return y >= rect.top && y <= rect.bottom && x >= rect.left && x <= rect.right;
};

/**
 * Find an SVGElement container at a given point
 *
 * @param {Number} x The x coordinate of the point
 * @param {Number} y The y coordinate of the point
 * @return {SVGElement} The container SVG or null if one can't be found
 */
export const findSVGAtPoint = (x, y) => {
  const elements = document.querySelectorAll(
    'svg[data-pdf-annotate-container="true"]'
  );

  for (let i = 0, l = elements.length; i < l; i++) {
    const el = elements[i];
    const rect = el.getBoundingClientRect();

    if (pointIntersectsRect(x, y, rect)) {
      return el;
    }
  }

  return null;
};

export function convertToSvgRect(rect, viewport) {
  let pt1 = [rect.x, rect.y];
  let pt2 = [rect.x + rect.width, rect.y + rect.height];

  pt1 = convertToSvgPoint(pt1, viewport);
  pt2 = convertToSvgPoint(pt2, viewport);

  return {
    x: Math.min(pt1[0], pt2[0]),
    y: Math.min(pt1[1], pt2[1]),
    width: Math.abs(pt2[0] - pt1[0]),
    height: Math.abs(pt2[1] - pt1[1]),
  };
}

/**
 * Get the x/y translation to be used for transforming the annotations
 * based on the rotation of the viewport.
 *
 * @param {Object} viewport The viewport data from the page
 * @return {Object}
 */
export const getTranslation = (viewport) => {
  let x;
  let y;

  // Modulus 360 on the rotation so that we only
  // have to worry about four possible values.
  switch (viewport.rotation % 360) {
    case 0:
      x = y = 0;
      break;
    case 90:
      x = 0;
      y = (viewport.width / viewport.scale) * -1;
      break;
    case 180:
      x = (viewport.width / viewport.scale) * -1;
      y = (viewport.height / viewport.scale) * -1;
      break;
    case 270:
      x = (viewport.height / viewport.scale) * -1;
      y = 0;
      break;
  }

  return { x, y };
};

const convertToSvgPoint = (pt, viewport) => {
  let xform = [1, 0, 0, 1, 0, 0];
  // xform = scale(xform, viewport.scale, viewport.scale);
  xform = rotate(xform, viewport.rotation);

  const offset = getTranslation(viewport);
  xform = translate(xform, offset.x, offset.y);

  return applyInverseTransform(pt, xform);
};
